// Podcast Ad's draft as the form keeps it (spec G14-templates, PR 16): the server holds the
// member's draft (`.../templates/podcast-ad/draft`), so it survives the panel; the form reads it,
// keeps its edits there, and asks the server's directors and stills (live's
// `usePodcastAdDraft`, which ran them from the browser).
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetPodcastAdDraftQueryKey,
  useApprovePodcastAdScript,
  useFixPodcastAdLabel,
  useGetPodcastAdDraft,
  useRegeneratePodcastAdStill,
  useResetPodcastAdDraft,
  useWritePodcastAdDraft,
  useWritePodcastAdScript,
  writePodcastAdDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  DraftStill,
  PodcastAdDraft,
  PodcastScript,
  StillName,
} from "../../../api/generated/model";
import {
  PodcastAdApproval,
  PodcastAdDraftWrite,
  PodcastAdInputs,
  PodcastAdScriptAsk,
  type PodcastAdDraftWriteOutput,
  type PodcastAdInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface DraftForm {
  inputs: PodcastAdInputsOutput;
  script: PodcastScript | null;
}

/** The cast's stills in live's order (`STILL_GRAPH.order`), and what each is drawn from. */
export const STILL_ORDER: readonly StillName[] = [
  "master",
  "host_close",
  "guest_close",
  "guest_hold",
  "end_card",
];
const STILL_SOURCES: Readonly<Partial<Record<StillName, readonly StillName[]>>> = {
  host_close: ["master"],
  guest_close: ["master"],
  guest_hold: ["guest_close"],
};

/** The draft refetched while it works. It is no job query (the stream moves those), so it is
 * polled: every 2 s while a director or a still works. */
const POLL_MS = 2_000;
/** A step's end moves the draft on a moment after its job ends (the outbox's
 * `advance_template_draft`): a still's retry starts, a director's answer is kept. The read keeps
 * polling this long after it last saw work, so that move is seen. */
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** "Generate video"'s body (live's hand-off, PR B): the approved script and spec, the five drawn
 * stills by reference and the form's captions; null until the cast is drawn. */
export function videoBody(draft: PodcastAdDraft, inputs: PodcastAdInputsOutput) {
  if (draft.stage !== "cast" || draft.script === null || draft.spec === null) return null;
  const output = (name: StillName) => draft.stills.find((each) => each.name === name)?.output ?? null;
  const [master, host_close, guest_close, guest_hold, end_card] = STILL_ORDER.map(output);
  if (!master || !host_close || !guest_close || !guest_hold || !end_card) return null;
  return {
    type: "video.template" as const,
    inputs: {
      template: "podcast-ad" as const,
      script: draft.script,
      spec: draft.spec,
      stills: { master, host_close, guest_close, guest_hold, end_card },
      subtitles: inputs.subtitles,
      caption_position: inputs.caption_position,
      caption_style_id: inputs.caption_style_id,
    },
  };
}

/** A still under way (live's `isStillWaiting`): drawing, or pending on stills that are all done
 * or still coming. A still pending on a failed one waits for nothing. */
export function stillWaiting(name: StillName, stills: readonly DraftStill[]): boolean {
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  if (status !== "pending") return false;
  const sources = STILL_SOURCES[name] ?? [];
  return (
    sources.length > 0 &&
    sources.every(
      (source) =>
        stills.find((each) => each.name === source)?.status === "done" || stillWaiting(source, stills),
    )
  );
}

/** Where the draft's work stands, as live's progress line names it. */
export interface DraftWork {
  /** The script director writes (or its answer is on its way: `asked` is the draft it answered
   * Generate script with, so it changes once the answer is kept or refused). */
  writing: boolean;
  /** The shot director casts the set; approved with no stills yet, its answer is on its way. */
  planning: boolean;
  /** A still draws, or waits on one that does. */
  drawing: boolean;
}

export function workOf(draft: PodcastAdDraft, asked: string | null): DraftWork {
  return {
    writing:
      draft.script_director === "working" ||
      (asked === draft.updated_at && draft.script_director !== "failed" && draft.stage === "script"),
    planning: draft.shot_director === "working" || (draft.stage === "cast" && draft.stills.length === 0),
    drawing: STILL_ORDER.some((name) => stillWaiting(name, draft.stills)),
  };
}

function working(work: DraftWork): boolean {
  return work.writing || work.planning || work.drawing;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: PodcastAdDraft): DraftForm {
  const inputs = PodcastAdInputs.safeParse(draft.inputs);
  return {
    inputs: inputs.success ? inputs.data : PodcastAdInputs.parse({}),
    script: draft.script,
  };
}

/** A refusal because the draft is locked: a director or a still works, or the script is
 * approved (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it. The form shows the draft as read; once the
 * member edits it (only in the script stage, nothing working), the edits are the form's and are
 * kept with PUT a moment after typing pauses (a 409 then only means the draft got locked: the
 * edits give way to it). Each ask (Generate script, Approve script, ↻, Fix label, start over)
 * answers with the draft, which the form shows from then on.
 */
export function usePodcastDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetPodcastAdDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<DraftForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetPodcastAdDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        // The stills' signed URLs are read again before they expire (`api/signed-urls.ts`).
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  /** The draft an ask answered with: shown from now on, the edits it carried now the server's. */
  const took = (answer: PodcastAdDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    // After a refusal, the draft as it is now (web/AGENTS.md).
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWritePodcastAdScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApprovePodcastAdScript(asks);
  const regeneration = useRegeneratePodcastAdStill(asks);
  const labelFix = useFixPodcastAdLabel(asks);
  const reset = useResetPodcastAdDraft(asks);
  const asking =
    script.isPending || approval.isPending || regeneration.isPending || labelFix.isPending || reset.isPending;
  const work: DraftWork =
    draft === undefined ? { writing: false, planning: false, drawing: false } : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWritePodcastAdDraft({
    mutation: {
      onSuccess: () => setFailure(null),
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  // The edits not kept yet, sent as the form closes so a pause shorter than the wait loses none.
  const unsaved = useRef<{ teamId: string; brandId: string; data: PodcastAdDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) {
        writePodcastAdDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
      }
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = PodcastAdDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    /** The form as shown: the edits, or the draft as read. */
    form,
    work,
    busy,
    /** The picks and the script can change: the script stage, nothing working. */
    editable,
    /** The last refused ask or save, said where the form shows it. */
    failure,
    asking,
    approving: approval.isPending,
    resetting: reset.isPending,
    /** Changes the form, from what it shows now. */
    edit(change: Partial<DraftForm>) {
      if (server === null) return;
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    /** Changes some picks, keeping the others as they are by then (two actors made at once). */
    editInputs(change: Partial<PodcastAdInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = PodcastAdScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = PodcastAdApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    regenerate(name: StillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    fixLabel() {
      ask();
      labelFix.mutate({ teamId, brandId });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
